import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Plus, Inbox, Users } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export function TopBar({ active }) {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    const fetchUnread = () => api.get("/leads/summary")
      .then(({ data }) => { if (alive) setUnread(data.unread_total || 0); })
      .catch(() => {});
    fetchUnread();
    const id = setInterval(fetchUnread, 25000);
    const onRefresh = () => fetchUnread();
    window.addEventListener("wt:leads-refresh", onRefresh);
    return () => { alive = false; clearInterval(id); window.removeEventListener("wt:leads-refresh", onRefresh); };
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-md border-b border-[#27272A]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <button onClick={() => navigate("/dashboard")} data-testid="topbar-logo">
          <Logo size={34} />
        </button>
        <div className="flex items-center gap-2">
          <a
            href="https://webtu.in"
            data-testid="topbar-home-link"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-zinc-400 hover:text-white transition-all"
            title="Back to webtu.in"
          >
            Home
          </a>
          <button
            data-testid="topbar-dashboard-btn"
            onClick={() => navigate("/dashboard")}
            className={`hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${active === "dashboard" ? "text-[#E5C378]" : "text-zinc-400 hover:text-white"}`}
          >
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </button>
          <button
            data-testid="topbar-leads-btn"
            onClick={() => navigate("/leads")}
            className={`relative inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-sm font-medium transition-all ${active === "leads" ? "text-[#E5C378]" : "text-zinc-400 hover:text-white"}`}
            title="Lead Inbox"
          >
            <Inbox className="w-4 h-4" /> <span className="hidden sm:inline">Leads</span>
            {unread > 0 && (
              <span data-testid="topbar-leads-unread-badge"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-[#C9A24B] text-[#0A0A0A] flex items-center justify-center shadow-[0_0_10px_rgba(201,162,75,.5)] wt-pulse">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
          <button
            data-testid="topbar-referrals-btn"
            onClick={() => navigate("/referrals")}
            className={`hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${active === "referrals" ? "text-[#E5C378]" : "text-zinc-400 hover:text-white"}`}
            title="Invite & Earn"
          >
            <Users className="w-4 h-4" /> Invite
          </button>
          <button
            data-testid="topbar-create-btn"
            onClick={() => navigate("/create")}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-sm font-semibold bg-[#1C1C1E] border border-[#27272A] hover:border-[#C9A24B]/50 transition-all"
          >
            <Plus className="w-4 h-4 text-[#C9A24B]" /> <span className="hidden sm:inline">New Site</span>
          </button>
          <button
            data-testid="topbar-logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="p-2 rounded-xl text-zinc-400 hover:text-white hover:bg-[#1C1C1E] transition-all"
            title={user?.phone ? `+91 ${user.phone}` : "Logout"}
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
